import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bell, X, Plus, Send } from "lucide-react";
import { toast } from "sonner";

export default function CommNotificationsPanel() {
  const [emails, setEmails] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [debounceMin, setDebounceMin] = useState(5);

  const load = async () => {
    try {
      const { data } = await api.get("/comms/notification-settings");
      setEmails(data?.notification_emails || []);
      setDebounceMin(data?.debounce_minutes || 5);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load notification settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const persist = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/comms/notification-settings", { notification_emails: next });
      setEmails(data?.notification_emails || []);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      await load();
    } finally {
      setSaving(false);
    }
  };

  const addEmail = async () => {
    const v = draft.trim().toLowerCase();
    if (!v) return;
    if (emails.includes(v)) {
      toast.info("Already in list");
      setDraft("");
      return;
    }
    const next = [...emails, v];
    setDraft("");
    await persist(next);
  };

  const removeEmail = async (email) => {
    const next = emails.filter((e) => e !== email);
    await persist(next);
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/comms/notification-settings/test", {});
      toast.success(`Test sent to ${data?.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <section
      className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp"
      data-testid="comm-notifications-panel"
    >
      <div className="flex items-center gap-2 mb-4">
        <Bell className="w-5 h-5 text-[#FF5A00]" />
        <h2 className="font-heading text-lg font-bold">Communication Notifications</h2>
      </div>
      <p className="text-sm text-zinc-500 mb-4">
        Email addresses that get notified when a customer sends you a new text. We bucket replies
        into one email per customer per {debounceMin} minutes so quick back-and-forths don't flood
        your inbox.
      </p>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : (
        <>
          {emails.length > 0 && (
            <ul className="flex flex-wrap gap-2 mb-4" data-testid="comm-notifications-list">
              {emails.map((e) => (
                <li
                  key={e}
                  className="inline-flex items-center gap-2 pl-3 pr-1 py-1 bg-zinc-100 rounded-full text-sm"
                  data-testid={`comm-notifications-chip-${e}`}
                >
                  <span className="font-mono-accent text-xs">{e}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${e}`}
                    disabled={saving}
                    onClick={() => removeEmail(e)}
                    className="w-5 h-5 rounded-full bg-white hover:bg-red-50 flex items-center justify-center transition-colors"
                    data-testid={`comm-notifications-remove-${e}`}
                  >
                    <X className="w-3 h-3 text-zinc-600" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex gap-2 mb-4">
            <Input
              type="email"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
              placeholder="you@example.com"
              className="flex-1"
              disabled={saving}
              data-testid="comm-notifications-email-input"
            />
            <Button
              onClick={addEmail}
              disabled={saving || !draft.trim()}
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
              data-testid="comm-notifications-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-zinc-100">
            <div className="text-xs text-zinc-500">
              {emails.length === 0
                ? "No addresses — add at least one to enable notifications."
                : `${emails.length} address${emails.length === 1 ? "" : "es"} will be notified.`}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={sendTest}
              disabled={testing || emails.length === 0}
              data-testid="comm-notifications-test-btn"
            >
              <Send className="w-4 h-4 mr-1" />
              {testing ? "Sending…" : "Send test email"}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
